import { useState, useMemo } from "react";
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { OrganizationFilter, useOrganizationFilter } from '@/components/admin/OrganizationFilter';
import { useAuditorCohortIds } from '@/hooks/useAuditorCohortIds';
import { useEnterpriseDashboard } from '@/hooks/useEnterpriseDashboard';
import type { ProjectUserLog, DiagnosticUserLog, NpsUserLog, DeliverableParticipant } from '@/hooks/useEnterpriseDashboard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Building2, 
  Users, 
  TrendingUp, 
  Target,
  Brain,
  DollarSign,
  PieChart,
  Zap,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ArrowUpRight,
  Gauge,
  Download,
  BarChart3,
  Activity,
  FileCheck,
  ThumbsUp,
  ThumbsDown,
  Meh,
  MessageSquare,
  Search,
  Eye,
  BookOpen,
  Lightbulb,
  Check,
  X,
  ExternalLink,
  Sparkles,
  Info,
  Rocket,
  ClipboardList,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  AreaChart,
  Area
} from "recharts";
import { BENEFIT_TYPE_LABELS } from '@/data/roiModelData';
import { useCohortFilterStore } from '@/hooks/useOrgFilterStore';
import { useOrgCurrency } from '@/hooks/useOrgCurrency';
import { formatCurrency as fmtCurrency } from '@/lib/formatCurrency';
import { DiagnosticAnalysisModal } from '@/components/admin/dashboard/DiagnosticAnalysisModal';
import { ProjectsAnalysisModal } from '@/components/admin/dashboard/ProjectsAnalysisModal';
import { EnterpriseSelfPacedTab } from '@/components/admin/EnterpriseSelfPacedTab';
import { EnterpriseRouteInterestsTab } from '@/components/admin/EnterpriseRouteInterestsTab';

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-64 rounded-lg" />
      <Skeleton className="h-96 rounded-lg" />
    </div>
  );
}

export default function EnterpriseDashboardPage() {
  const { currentRole, profile } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  // El Auditor elige empresa/cohorte libremente (limitado por RLS a lo asignado).
  const isAuditor = currentRole === 'auditor';
  const canSelectOrg = isSuperAdmin || isAuditor;
  const { cohortIds: auditorCohortIds } = useAuditorCohortIds();

  const [selectedOrg, setSelectedOrg] = useState<string>(() =>
    canSelectOrg ? 'all' : (profile?.organization_id || '')
  );
  const [selectedCohort, setSelectedCohort] = useCohortFilterStore();
  const { getOrgName } = useOrganizationFilter(selectedOrg);

  const effectiveOrg = canSelectOrg ? selectedOrg : (profile?.organization_id || '');
  const currency = useOrgCurrency(effectiveOrg);
  const formatCurrency = (v: number) => fmtCurrency(v, currency);
  // (state below)

  // Tab Proyectos: filtrar solo con entregable
  const [projOnlyWithDeliverable, setProjOnlyWithDeliverable] = useState(false);
  const [projectsAnalysisOpen, setProjectsAnalysisOpen] = useState(false);

  const {
    cohorts,
    diagnostics: diagnosticData,
    cohortData,
    projects: projectResults,
    adoption: adoptionData,
    tools: toolsUsageData,
    nps: npsData,
    roi: roiData,
    isLoading,
  } = useEnterpriseDashboard(effectiveOrg, selectedCohort, projOnlyWithDeliverable);

  // NPS Filters
  const [npsFilterCohort, setNpsFilterCohort] = useState("all");
  const [npsFilterCompany, setNpsFilterCompany] = useState("all");
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [npsFilterType, setNpsFilterType] = useState("all");

  // Diagnostic Log Filters
  const [diagFilterStatus, setDiagFilterStatus] = useState("all");
  const [diagFilterArea, setDiagFilterArea] = useState("all");
  const [diagSearchTerm, setDiagSearchTerm] = useState("");

  // Project Log Filters
  const [projFilterStatus, setProjFilterStatus] = useState("all");
  const [projFilterArea, setProjFilterArea] = useState("all");
  const [projSearchTerm, setProjSearchTerm] = useState("");

  // Project detail dialog
  const [selectedProjectUser, setSelectedProjectUser] = useState<ProjectUserLog | null>(null);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);

  // Deliverable filter (by module)
  const [deliverableFilter, setDeliverableFilter] = useState<string>("all");
  const [deliverableStatusFilter, setDeliverableStatusFilter] = useState<string>("all");

  // Progress scope toggle (Total vs current week)
  const [progressScopeMode, setProgressScopeMode] = useState<'total' | 'current_week'>('total');

  // Detail modals for progress
  const [contentModalUser, setContentModalUser] = useState<ScopedParticipant | null>(null);
  const [deliverableModalUser, setDeliverableModalUser] = useState<ScopedParticipant | null>(null);
  const [projectsModalUser, setProjectsModalUser] = useState<ScopedParticipant | null>(null);

  const navigate = useNavigate();

  // Diagnostic detail dialog
  const [selectedDiagUser, setSelectedDiagUser] = useState<DiagnosticUserLog | null>(null);
  const [isDiagDialogOpen, setIsDiagDialogOpen] = useState(false);

  // Compute scoped progress per participant (filter content/deliverables by week if needed)
  const scopedParticipants = useMemo(() => {
    const cw = cohortData.currentWeekNumber;
    const filterByWeek = progressScopeMode === 'current_week' && cw > 0;
    const inScope = (wn: number | null) => !filterByWeek || wn === null || wn <= cw;
    return cohortData.participants.map(p => {
      const scopedContent = p.allContentItems.filter(c => inScope(c.weekNumber));
      const contentDone = scopedContent.filter(c => c.completed).length;
      const contentPercent = scopedContent.length > 0 ? Math.round((contentDone / scopedContent.length) * 100) : 0;
      const scopedModules = p.modules.filter(m => inScope(m.weekNumber));
      const deliverablesSubmitted = scopedModules.filter(m => m.status === 'entregado').length;
      return {
        ...p,
        scopedContent,
        contentPercent,
        scopedModules,
        deliverablesSubmitted,
        deliverablesTotal: scopedModules.length,
      };
    });
  }, [cohortData.participants, cohortData.currentWeekNumber, progressScopeMode]);

  type ScopedParticipant = (typeof scopedParticipants)[number];

  const universeInfo = useMemo(() => {
    const sample = scopedParticipants[0];
    if (!sample) return { contents: 0, deliverables: 0 };
    return { contents: sample.scopedContent.length, deliverables: sample.scopedModules.length };
  }, [scopedParticipants]);

  // Cohort summary cards (scope-aware)

  const scopedCompletedDeliveries = useMemo(
    () => scopedParticipants.reduce((s, p) => s + p.deliverablesSubmitted, 0),
    [scopedParticipants]
  );

  // KPIs X/Y for Cohort tab
  const deliverablesKpi = useMemo(() => {
    const submitted = scopedParticipants.reduce((s, p) => s + p.deliverablesSubmitted, 0);
    const total = scopedParticipants.reduce((s, p) => s + p.deliverablesTotal, 0);
    return { submitted, total };
  }, [scopedParticipants]);

  const projectsKpi = useMemo(() => {
    const all = scopedParticipants.flatMap(p => p.projectItems || []);
    return { submitted: all.filter(p => p.submitted).length, created: all.length };
  }, [scopedParticipants]);

  const diagnosticsKpi = useMemo(() => {
    const total = scopedParticipants.length;
    const completed = scopedParticipants.filter(p => p.diagnosticCompleted).length;
    return { completed, total };
  }, [scopedParticipants]);



  const handleOpenProjectDetail = (user: ProjectUserLog) => {
    if (user.hasProject) {
      setSelectedProjectUser(user);
      setIsProjectDialogOpen(true);
    }
  };

  const handleOpenDiagDetail = (user: DiagnosticUserLog) => {
    if (user.completed && user.responses) {
      setSelectedDiagUser(user);
      setIsDiagDialogOpen(true);
    }
  };

  // Get unique areas from diagnostic data
  const diagAreas = [...new Set(diagnosticData.userLog.map(u => u.area))];
  const projAreas = [...new Set(projectResults.userLog.map(u => u.area))];

  // Filter diagnostic users
  const filteredDiagUsers = diagnosticData.userLog.filter(user => {
    const matchesStatus = diagFilterStatus === "all" || 
      (diagFilterStatus === "completed" && user.completed) || 
      (diagFilterStatus === "pending" && !user.completed);
    const matchesArea = diagFilterArea === "all" || user.area === diagFilterArea;
    const matchesSearch = diagSearchTerm === "" || 
      user.name.toLowerCase().includes(diagSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(diagSearchTerm.toLowerCase());
    return matchesStatus && matchesArea && matchesSearch;
  });

  // Filter project users
  const filteredProjUsers = projectResults.userLog.filter(user => {
    const matchesStatus = projFilterStatus === "all" || 
      (projFilterStatus === "hasProject" && user.hasProject) || 
      (projFilterStatus === "noProject" && !user.hasProject);
    const matchesArea = projFilterArea === "all" || user.area === projFilterArea;
    const matchesSearch = projSearchTerm === "" || 
      user.name.toLowerCase().includes(projSearchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(projSearchTerm.toLowerCase()) ||
      (user.projectName && user.projectName.toLowerCase().includes(projSearchTerm.toLowerCase()));
    return matchesStatus && matchesArea && matchesSearch;
  });

  // NPS filters
  const npsCompanies = [...new Set(npsData.byUser.map(u => u.company).filter(Boolean))];
  const npsCohorts = [...new Set(npsData.byUser.map(u => u.cohort).filter(Boolean))];

  const filteredNpsUsers = npsData.byUser.filter(user => {
    const matchesCohort = npsFilterCohort === "all" || user.cohort === npsFilterCohort;
    const matchesCompany = npsFilterCompany === "all" || user.company === npsFilterCompany;
    const matchesType = npsFilterType === "all" || user.type === npsFilterType;
    return matchesCohort && matchesCompany && matchesType;
  });

  // CSV exports
  const handleExportDiagnosticLog = () => {
    const headers = ["Nombre", "Email", "Área", "Estado", "Fecha Completado", "Nivel", "Score"];
    const rows = filteredDiagUsers.map(u => [
      u.name, u.email, u.area,
      u.completed ? "Completado" : "Pendiente",
      u.completedAt ? new Date(u.completedAt).toLocaleDateString('es-ES') : "—",
      u.level || "—",
      u.score !== null ? `${u.score}/5` : "—"
    ]);
    downloadCsv(headers, rows, `diagnostico_log_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleExportProjectLog = () => {
    const headers = ["Nombre", "Email", "Área", "Estado", "Nombre Proyecto", "Estado Proyecto", "Fecha Creación"];
    const rows = filteredProjUsers.map(u => [
      u.name, u.email, u.area,
      u.hasProject ? "Con proyecto" : "Sin proyecto",
      u.projectName || "—",
      u.projectStatus || "—",
      u.createdAt ? new Date(u.createdAt).toLocaleDateString('es-ES') : "—"
    ]);
    downloadCsv(headers, rows, `proyectos_log_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const handleExportProgress = () => {
    if (scopedParticipants.length === 0) return;
    const rows = scopedParticipants.map(p => {
      const done = p.scopedModules.filter(m => m.status === 'entregado').map(m => m.moduleName).join(', ');
      return {
        'Participante': p.name,
        'Correo': p.email,
        'Empresa': p.company,
        'Área': p.area,
        '% Contenido': p.contentPercent,
        'Entregables (enviados/total)': `${p.deliverablesSubmitted}/${p.deliverablesTotal}`,
        'Entregables realizados': done,
        'Diagnóstico': p.diagnosticCompleted ? 'Sí' : 'No',
        'Proyectos creados': p.projectItems.length,
        'Proyectos enviados': p.projectItems.filter(pr => pr.submitted).length,
        'Lab. Retos': p.challengeLabSolutions > 0 ? 'Sí' : 'No',
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Object.keys(rows[0]).map(k => ({ wch: Math.max(k.length, 18) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Progreso');
    const cohortName = cohorts.find(c => c.id === selectedCohort)?.name || 'todas';
    const safeName = cohortName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const scope = progressScopeMode === 'total' ? 'total' : `semana-${cohortData.currentWeekNumber}`;
    const date = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `progreso_${safeName}_${scope}_${date}.xlsx`);
  };

  const handleExportNPS = () => {
    const typeLabels = { promoter: "Promotor", passive: "Pasivo", detractor: "Detractor" };
    const headers = ["Participante", "Empresa", "Cohorte", "Área", "Puntuación", "Tipo", "Fecha"];
    const rows = filteredNpsUsers.map(u => [
      u.name, u.company, u.cohort, u.area,
      String(u.score),
      typeLabels[u.type],
      new Date(u.date).toLocaleDateString('es-ES')
    ]);
    downloadCsv(headers, rows, `nps_export_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // PDF export per tab
  const handleExportTabPDF = async (tab: string) => {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const dateStr = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    const orgName = getOrgName() || 'Todas las organizaciones';

    const addHeader = (title: string, subtitle?: string) => {
      doc.setFontSize(20); doc.setTextColor(40, 40, 40);
      doc.text(title, 14, 22);
      doc.setFontSize(11); doc.setTextColor(100, 100, 100);
      doc.text(subtitle || orgName, 14, 30);
      doc.setFontSize(9); doc.setTextColor(150, 150, 150);
      doc.text(`Generado: ${dateStr}`, 14, 36);
    };

    const addFooter = () => {
      const pc = doc.getNumberOfPages();
      for (let i = 1; i <= pc; i++) {
        doc.setPage(i); doc.setFontSize(8); doc.setTextColor(180, 180, 180);
        doc.text(`LIDER-IA — Dashboard Empresarial — Página ${i} de ${pc}`, pw / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });
      }
    };

    if (tab === 'diagnostics') {
      addHeader('Dashboard Empresarial — Diagnósticos');
      let y = 46;
      const items = [
        { label: 'Total Participantes', value: diagnosticData.totalParticipants },
        { label: 'Diagnósticos Completados', value: diagnosticData.completedDiagnostics },
        { label: 'Score Promedio', value: `${diagnosticData.averageScore}/5` },
        { label: 'Tasa de Completado', value: `${diagnosticData.totalParticipants > 0 ? Math.round((diagnosticData.completedDiagnostics / diagnosticData.totalParticipants) * 100) : 0}%` },
      ];
      items.forEach(i => { doc.setFontSize(10); doc.setTextColor(80,80,80); doc.text(`${i.label}:`, 14, y); doc.setTextColor(40,40,40); doc.text(String(i.value), 70, y); y += 6; });
      y += 6;
      if (diagnosticData.levelDistribution.length > 0) {
        doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Distribución por Nivel', 14, y); y += 4;
        autoTable(doc, { head: [['Nivel', 'Cantidad']], body: diagnosticData.levelDistribution.map(l => [`N${l.levelNumber} ${l.level}`, String(l.count)]), startY: y, styles: { fontSize: 9 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, margin: { left: 14, right: 14 } });
        y = (doc as any).lastAutoTable.finalY + 8;
      }
      if (y > 220) { doc.addPage(); y = 20; }
      doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Log de Usuarios', 14, y); y += 4;
      autoTable(doc, { head: [['Nombre', 'Área', 'Estado', 'Nivel', 'Score']], body: filteredDiagUsers.map(u => [u.name, u.area, u.completed ? 'Completado' : 'Pendiente', u.level || '—', u.score !== null ? `${u.score}/5` : '—']), startY: y, styles: { fontSize: 8 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, alternateRowStyles: { fillColor: [245,245,250] }, margin: { left: 14, right: 14 } });
    }

    else if (tab === 'cohort') {
      addHeader('Dashboard Empresarial — Reporte de Progreso');
      let y = 46;
      const scopeLabel = progressScopeMode === 'total' ? 'Todo la ruta' : `Hasta semana ${cohortData.currentWeekNumber}`;
      const items = [
        { label: 'Alcance', value: scopeLabel },
        { label: 'Entregables', value: `${deliverablesKpi.submitted}/${deliverablesKpi.total}` },
        { label: 'Proyectos', value: `${projectsKpi.submitted}/${projectsKpi.created}` },
        { label: 'Diagnósticos', value: `${diagnosticsKpi.completed}/${diagnosticsKpi.total}` },
        { label: 'Semana Actual', value: cohortData.currentWeek },
      ];
      items.forEach(i => { doc.setFontSize(10); doc.setTextColor(80,80,80); doc.text(`${i.label}:`, 14, y); doc.setTextColor(40,40,40); doc.text(String(i.value), 70, y); y += 6; });
      y += 6;
      if (scopedParticipants.length > 0) {
        doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Reporte de Progreso', 14, y); y += 4;
        autoTable(doc, {
          head: [['Participante', 'Empresa', '% Cont.', 'Entregables', 'Diag.', 'Proyectos', 'Lab. Retos']],
          body: scopedParticipants.map(p => [
            p.name,
            p.company,
            `${p.contentPercent}%`,
            `${p.deliverablesSubmitted}/${p.deliverablesTotal}`,
            p.diagnosticCompleted ? 'Sí' : 'No',
            `${p.projectItems.filter(pr => pr.submitted).length}/${p.projectItems.length}`,
            p.challengeLabSolutions > 0 ? 'Sí' : 'No',
          ]),
          startY: y, styles: { fontSize: 8 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, alternateRowStyles: { fillColor: [245,245,250] }, margin: { left: 14, right: 14 }
        });
      }
    }

    else if (tab === 'projects') {
      addHeader('Dashboard Empresarial — Proyectos');
      let y = 46;
      const withP = projectResults.userLog.filter(u => u.hasProject).length;
      const withoutP = projectResults.userLog.filter(u => !u.hasProject).length;
      const items = [
        { label: 'Total Proyectos', value: projectResults.total },
        { label: 'Con Proyecto', value: withP },
        { label: 'Sin Proyecto', value: withoutP },
      ];
      items.forEach(i => { doc.setFontSize(10); doc.setTextColor(80,80,80); doc.text(`${i.label}:`, 14, y); doc.setTextColor(40,40,40); doc.text(String(i.value), 70, y); y += 6; });
      y += 6;
      if (projectResults.topProjects.length > 0) {
        doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Top Proyectos', 14, y); y += 4;
        autoTable(doc, { head: [['Proyecto', 'Área', 'Impacto', 'Score']], body: projectResults.topProjects.map(p => [p.name, p.area, p.impact, String(p.score)]), startY: y, styles: { fontSize: 9 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, margin: { left: 14, right: 14 } });
        y = (doc as any).lastAutoTable.finalY + 8;
      }
      if (y > 220) { doc.addPage(); y = 20; }
      doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Log de Usuarios', 14, y); y += 4;
      autoTable(doc, { head: [['Nombre', 'Área', 'Estado', 'Proyecto', 'Estado Proy.']], body: filteredProjUsers.map(u => [u.name, u.area, u.hasProject ? 'Con proyecto' : 'Sin proyecto', u.projectName || '—', u.projectStatus || '—']), startY: y, styles: { fontSize: 8 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, alternateRowStyles: { fillColor: [245,245,250] }, margin: { left: 14, right: 14 } });
    }

    else if (tab === 'adoption') {
      addHeader('Dashboard Empresarial — Adopción');
      let y = 46;
      const b = adoptionData.breakdown;
      const items = [
        { label: 'Adopción General (promedio)', value: `${adoptionData.overallAdoption}%` },
        { label: 'Entregables', value: b.deliverables ? `${b.deliverables.ratio}% (${b.deliverables.numerator}/${b.deliverables.denominator})` : 'N/A (sin cohorte)' },
        { label: 'Proyectos entregados', value: `${b.projects.ratio}% (${b.projects.numerator}/${b.projects.denominator})` },
        { label: 'Diagnósticos', value: `${b.diagnostics.ratio}% (${b.diagnostics.numerator}/${b.diagnostics.denominator})` },
        { label: 'Lab. Retos', value: `${b.challengeLab.ratio}% (${b.challengeLab.numerator}/${b.challengeLab.denominator})` },
        { label: 'Usuarios Activos (7 días)', value: adoptionData.weeklyActiveUsers },
        { label: 'Tasa de Completado', value: `${adoptionData.completionRate}%` },
      ];
      items.forEach(i => { doc.setFontSize(10); doc.setTextColor(80,80,80); doc.text(`${i.label}:`, 14, y); doc.setTextColor(40,40,40); doc.text(String(i.value), 70, y); y += 6; });
      y += 6;
      if (adoptionData.byDepartment.length > 0) {
        doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Adopción por Departamento', 14, y); y += 4;
        autoTable(doc, { head: [['Departamento', 'Adopción %']], body: adoptionData.byDepartment.map(d => [d.dept, `${d.adoption}%`]), startY: y, styles: { fontSize: 9 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, margin: { left: 14, right: 14 } });
      }
    }

    else if (tab === 'tools') {
      addHeader('Dashboard Empresarial — Herramientas');
      let y = 46;
      const items = [
        { label: 'Herramientas Totales', value: toolsUsageData.totalTools },
        { label: 'En Uso Activo', value: toolsUsageData.activeTools },
        { label: 'Bajo Uso', value: toolsUsageData.totalTools - toolsUsageData.activeTools },
      ];
      items.forEach(i => { doc.setFontSize(10); doc.setTextColor(80,80,80); doc.text(`${i.label}:`, 14, y); doc.setTextColor(40,40,40); doc.text(String(i.value), 70, y); y += 6; });
      y += 6;
      if (toolsUsageData.topTools.length > 0) {
        doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Top Herramientas', 14, y); y += 4;
        autoTable(doc, { head: [['Herramienta', 'Categoría', 'Uso %']], body: toolsUsageData.topTools.map(t => [t.name, t.category, `${t.usage}%`]), startY: y, styles: { fontSize: 9 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, margin: { left: 14, right: 14 } });
      }
    }

    else if (tab === 'nps') {
      addHeader('Dashboard Empresarial — NPS');
      let y = 46;
      const items = [
        { label: 'NPS Actual', value: npsData.currentNPS },
        { label: 'Total Respuestas', value: npsData.totalResponses },
        { label: 'Promotores', value: npsData.distribution.promoters },
        { label: 'Pasivos', value: npsData.distribution.passives },
        { label: 'Detractores', value: npsData.distribution.detractors },
      ];
      items.forEach(i => { doc.setFontSize(10); doc.setTextColor(80,80,80); doc.text(`${i.label}:`, 14, y); doc.setTextColor(40,40,40); doc.text(String(i.value), 70, y); y += 6; });
      y += 6;
      if (npsData.byArea.length > 0) {
        doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('NPS por Área', 14, y); y += 4;
        autoTable(doc, { head: [['Área', 'NPS', 'Respuestas']], body: npsData.byArea.map(a => [a.area, String(a.nps), String(a.responses)]), startY: y, styles: { fontSize: 9 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, margin: { left: 14, right: 14 } });
        y = (doc as any).lastAutoTable.finalY + 8;
      }
      if (y > 220) { doc.addPage(); y = 20; }
      const typeLabels: Record<string, string> = { promoter: 'Promotor', passive: 'Pasivo', detractor: 'Detractor' };
      doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Detalle por Usuario', 14, y); y += 4;
      autoTable(doc, { head: [['Participante', 'Empresa', 'Área', 'Score', 'Tipo']], body: filteredNpsUsers.map(u => [u.name, u.company, u.area, String(u.score), typeLabels[u.type]]), startY: y, styles: { fontSize: 8 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, alternateRowStyles: { fillColor: [245,245,250] }, margin: { left: 14, right: 14 } });
    }

    else if (tab === 'roi') {
      addHeader('Dashboard Empresarial — ROI');
      let y = 46;
      const items = [
        { label: 'Inversión Total', value: formatCurrency(roiData.totalInvestment) },
        { label: 'Beneficios Totales', value: formatCurrency(roiData.totalBenefits) },
        { label: 'ROI', value: `${roiData.roiPercentage}%` },
        { label: 'BCR', value: `${roiData.bcrRatio}x` },
        { label: 'Payback', value: `${roiData.paybackMonths} meses` },
      ];
      items.forEach(i => { doc.setFontSize(10); doc.setTextColor(80,80,80); doc.text(`${i.label}:`, 14, y); doc.setTextColor(40,40,40); doc.text(String(i.value), 70, y); y += 6; });
      y += 6;
      if (roiData.costBreakdown.length > 0) {
        doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Desglose de Costos', 14, y); y += 4;
        autoTable(doc, { head: [['Categoría', 'Monto']], body: roiData.costBreakdown.map(c => [c.category, formatCurrency(c.amount)]), startY: y, styles: { fontSize: 9 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, margin: { left: 14, right: 14 } });
        y = (doc as any).lastAutoTable.finalY + 8;
      }
      if (roiData.benefitBreakdown.length > 0) {
        if (y > 220) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Inventario de Beneficios', 14, y); y += 4;
        autoTable(doc, { head: [['Categoría', 'Monto']], body: roiData.benefitBreakdown.map(b => [b.category, formatCurrency(b.amount)]), startY: y, styles: { fontSize: 9 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, margin: { left: 14, right: 14 } });
        y = (doc as any).lastAutoTable.finalY + 8;
      }
      if (roiData.projects.length > 0) {
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(12); doc.setTextColor(40,40,40); doc.text('Top Proyectos por ROI', 14, y); y += 4;
        autoTable(doc, { head: [['Proyecto', 'Empresa', 'Costos', 'Beneficios', 'ROI', 'BCR']], body: [...roiData.projects].sort((a,b) => b.roi - a.roi).map(p => [p.projectName, p.company, formatCurrency(p.totalCosts), formatCurrency(p.totalBenefits), `${Math.round(p.roi)}%`, `${p.bcr.toFixed(2)}x`]), startY: y, styles: { fontSize: 8 }, headStyles: { fillColor: [79,70,229], textColor: 255 }, alternateRowStyles: { fillColor: [245,245,250] }, margin: { left: 14, right: 14 } });
      }
    }

    addFooter();
    doc.save(`dashboard-${tab}-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const getStatusIcon = (status: "entregado" | "pendiente" | "tardío") => {
    switch (status) {
      case "entregado": return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "pendiente": return <Clock className="h-4 w-4 text-slate-400" />;
      case "tardío": return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
  };

  const getScoreColor = (score: number | null) => {
    if (score === null) return "text-muted-foreground";
    if (score >= 90) return "text-green-600";
    if (score >= 80) return "text-blue-600";
    if (score >= 70) return "text-amber-600";
    return "text-red-600";
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Building2 className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Dashboard Empresarial</h1>
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 className="h-8 w-8 text-primary" />
            Dashboard Empresarial
          </h1>
          <p className="text-muted-foreground mt-1">
            Panel de control ejecutivo para la gestión del portafolio de IA
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canSelectOrg && (
            <OrganizationFilter selectedOrg={selectedOrg} onOrgChange={setSelectedOrg} />
          )}
          <Select value={selectedCohort} onValueChange={setSelectedCohort}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Seleccionar cohorte" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las cohortes</SelectItem>
              {cohorts
                .filter((cohort) => !isAuditor || auditorCohortIds.includes(cohort.id))
                .map((cohort) => (
                <SelectItem key={cohort.id} value={cohort.id}>
                  {cohort.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Users className="h-5 w-5 text-blue-500" />
            </div>
            <div className="mt-2">
              <p className="text-2xl font-bold">{diagnosticData.totalParticipants}</p>
              <p className="text-xs text-muted-foreground">Participantes</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Target className="h-5 w-5 text-green-500" />
            </div>
            <div className="mt-2">
              <p className="text-2xl font-bold">{projectResults.total}</p>
              <p className="text-xs text-muted-foreground">Proyectos</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Brain className="h-5 w-5 text-purple-500" />
            </div>
            <div className="mt-2">
              <p className="text-2xl font-bold">{diagnosticData.averageScore}/5</p>
              <p className="text-xs text-muted-foreground">Score promedio</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Gauge className="h-5 w-5 text-amber-500" />
            </div>
            <div className="mt-2">
              <p className="text-2xl font-bold">{adoptionData.overallAdoption}%</p>
              <p className="text-xs text-muted-foreground">Adopción</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-cyan-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <Zap className="h-5 w-5 text-cyan-500" />
            </div>
            <div className="mt-2">
              <p className="text-2xl font-bold">{toolsUsageData.activeTools}</p>
              <p className="text-xs text-muted-foreground">Herramientas activas</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <DollarSign className="h-5 w-5 text-emerald-500" />
              {roiData.roiPercentage > 0 && (
                <Badge variant="secondary" className="text-xs bg-emerald-500/20 text-emerald-600">
                  {roiData.roiPercentage}%
                </Badge>
              )}
            </div>
            <div className="mt-2">
              <p className="text-2xl font-bold">{formatCurrency(roiData.totalBenefits)}</p>
              <p className="text-xs text-muted-foreground">ROI estimado</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="diagnostics" className="space-y-4">
        <TabsList className="grid grid-cols-4 lg:grid-cols-9 w-full">
          <TabsTrigger value="diagnostics" className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            <span className="hidden sm:inline">Diagnósticos</span>
          </TabsTrigger>
          <TabsTrigger value="cohort" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Cohorte</span>
          </TabsTrigger>
          <TabsTrigger value="projects" className="flex items-center gap-2">
            <Rocket className="h-4 w-4" />
            <span className="hidden sm:inline">Proyectos</span>
          </TabsTrigger>
          <TabsTrigger value="adoption" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">Adopción</span>
          </TabsTrigger>
          <TabsTrigger value="tools" className="flex items-center gap-2">
            <Zap className="h-4 w-4" />
            <span className="hidden sm:inline">Herramientas</span>
          </TabsTrigger>
          <TabsTrigger value="nps" className="flex items-center gap-2">
            <ThumbsUp className="h-4 w-4" />
            <span className="hidden sm:inline">NPS</span>
          </TabsTrigger>
          <TabsTrigger value="roi" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            <span className="hidden sm:inline">ROI</span>
          </TabsTrigger>
          <TabsTrigger value="self_paced" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Autoaprendizaje</span>
          </TabsTrigger>
          <TabsTrigger value="route_interests" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Interesados en rutas</span>
          </TabsTrigger>
        </TabsList>

        {/* Self-paced routes Tab */}
        <TabsContent value="self_paced" className="space-y-4">
          <EnterpriseSelfPacedTab organizationId={effectiveOrg} />
        </TabsContent>

        {/* Route interests Tab */}
        <TabsContent value="route_interests" className="space-y-4">
          <EnterpriseRouteInterestsTab organizationId={effectiveOrg} />
        </TabsContent>

        {/* Diagnostics Tab */}
        <TabsContent value="diagnostics" className="space-y-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setAnalysisOpen(true)}
              variant="default"
              size="sm"
              className="gap-2"
              disabled={!effectiveOrg || effectiveOrg === 'all'}
              title={!effectiveOrg || effectiveOrg === 'all' ? 'Selecciona una empresa para analizar' : undefined}
            >
              <Sparkles className="h-4 w-4" />
              Análisis de respuestas
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución por Nivel</CardTitle>
                <CardDescription>Clasificación de participantes según diagnóstico</CardDescription>
              </CardHeader>
              <CardContent>
                {diagnosticData.levelDistribution.length > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={diagnosticData.levelDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="level"
                          label={({ levelNumber, level, count }) => `N${levelNumber} ${level}: ${count}`}
                        >
                          {diagnosticData.levelDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin datos de diagnóstico</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Score por Área</CardTitle>
                <CardDescription>Promedio de diagnóstico por departamento</CardDescription>
              </CardHeader>
              <CardContent>
                {diagnosticData.byArea.length > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={diagnosticData.byArea}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="area" tick={{ fontSize: 12 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 5]} />
                        <Radar
                          name="Score"
                          dataKey="score"
                          stroke="hsl(var(--primary))"
                          fill="hsl(var(--primary))"
                          fillOpacity={0.5}
                        />
                        <RechartsTooltip />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin datos por área</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Resumen de Diagnósticos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-3xl font-bold text-primary">{diagnosticData.totalParticipants}</p>
                  <p className="text-sm text-muted-foreground">Total participantes</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-3xl font-bold text-green-500">{diagnosticData.completedDiagnostics}</p>
                  <p className="text-sm text-muted-foreground">Diagnósticos completados</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-3xl font-bold text-amber-500">{diagnosticData.averageScore}/5</p>
                  <p className="text-sm text-muted-foreground">Score promedio</p>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <p className="text-3xl font-bold text-purple-500">
                    {diagnosticData.totalParticipants > 0 ? Math.round((diagnosticData.completedDiagnostics / diagnosticData.totalParticipants) * 100) : 0}%
                  </p>
                  <p className="text-sm text-muted-foreground">Tasa de completado</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* User Log Table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Log de Usuarios - Diagnóstico
                </CardTitle>
                <CardDescription>Estado de completitud del diagnóstico por usuario</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleExportDiagnosticLog} variant="outline" size="sm" className="gap-2">
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
                <Button onClick={() => handleExportTabPDF('diagnostics')} variant="outline" size="sm" className="gap-2">
                  <FileCheck className="h-4 w-4" />
                  PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nombre o email..."
                    value={diagSearchTerm}
                    onChange={(e) => setDiagSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={diagFilterStatus} onValueChange={setDiagFilterStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="completed">Completados</SelectItem>
                    <SelectItem value="pending">Pendientes</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={diagFilterArea} onValueChange={setDiagFilterArea}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Área" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las áreas</SelectItem>
                    {diagAreas.map(area => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-md border overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Área</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Nivel</TableHead>
                      <TableHead className="text-right">Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDiagUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No se encontraron usuarios
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDiagUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{user.email}</TableCell>
                          <TableCell>{user.area}</TableCell>
                          <TableCell className="text-center">
                            {user.completed ? (
                              <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Completado
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-amber-500/10 text-amber-600">
                                <Clock className="h-3 w-3 mr-1" />
                                Pendiente
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {user.completedAt ? new Date(user.completedAt).toLocaleDateString('es-ES') : "—"}
                          </TableCell>
                          <TableCell>
                            {user.level ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => handleOpenDiagDetail(user)}
                                      className="cursor-pointer group flex items-center gap-2"
                                    >
                                      <Badge variant="outline" className="group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                                        {user.level}
                                      </Badge>
                                      <Eye className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Ver detalles</p></TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {user.score !== null ? (
                              <span className={user.score >= 4 ? "text-primary" : user.score >= 3 ? "text-secondary" : "text-muted-foreground"}>
                                {user.score}/5
                              </span>
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Mostrando {filteredDiagUsers.length} de {diagnosticData.userLog.length} usuarios
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cohort Tracking Tab */}
        <TabsContent value="cohort" className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/10 rounded-full">
                    <CheckCircle2 className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {deliverablesKpi.submitted}<span className="text-muted-foreground text-base font-normal"> / {deliverablesKpi.total}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">Entregables (entregados / posibles)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500/10 rounded-full">
                    <Rocket className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {projectsKpi.submitted}<span className="text-muted-foreground text-base font-normal"> / {projectsKpi.created}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">Proyectos (entregados / creados)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-teal-500/10 rounded-full">
                    <ClipboardList className="h-6 w-6 text-teal-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {diagnosticsKpi.completed}<span className="text-muted-foreground text-base font-normal"> / {diagnosticsKpi.total}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">Diagnósticos (realizados / participantes)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500/10 rounded-full">
                    <Clock className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{cohortData.currentWeek}</p>
                    <p className="text-sm text-muted-foreground">Progreso actual</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>


          {/* Progress Report Table */}
          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-primary" />
                  Reporte de Progreso
                </CardTitle>
                <CardDescription>Progreso integral por participante: contenido, entregables, diagnóstico, proyectos y lab. de retos</CardDescription>
              </div>
              <div className="flex flex-col items-start sm:items-end gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tabs value={progressScopeMode} onValueChange={(v) => setProgressScopeMode(v as 'total' | 'current_week')}>
                    <TabsList>
                      <TabsTrigger value="total">Total de la ruta</TabsTrigger>
                      <TabsTrigger value="current_week" disabled={cohortData.currentWeekNumber === 0}>
                        {cohortData.currentWeekNumber === 0 ? 'No iniciado' : `Hasta semana ${cohortData.currentWeekNumber}`}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Select value={deliverableFilter} onValueChange={setDeliverableFilter}>
                    <SelectTrigger className="w-[200px] h-9"><SelectValue placeholder="Entregable" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los entregables</SelectItem>
                      {(scopedParticipants[0]?.scopedModules || []).map((m, i) => (
                        <SelectItem key={m.moduleId} value={m.moduleId}>M{i + 1} — {m.moduleName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {deliverableFilter !== "all" && (
                    <Select value={deliverableStatusFilter} onValueChange={setDeliverableStatusFilter}>
                      <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="Estado" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="entregado">Entregaron</SelectItem>
                        <SelectItem value="pendiente">No entregaron</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <Button onClick={handleExportProgress} variant="outline" size="sm" className="gap-2" disabled={scopedParticipants.length === 0}>
                    <Download className="h-4 w-4" />
                    Excel
                  </Button>
                  <Button onClick={() => handleExportTabPDF('cohort')} variant="outline" size="sm" className="gap-2" disabled={scopedParticipants.length === 0}>
                    <FileCheck className="h-4 w-4" />
                    PDF
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {progressScopeMode === 'total'
                    ? `Mostrando todo la ruta (${universeInfo.contents} contenidos, ${universeInfo.deliverables} entregables)`
                    : `Mostrando hasta semana ${cohortData.currentWeekNumber} (${universeInfo.contents} contenidos, ${universeInfo.deliverables} entregables)`}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              {cohortData.participants.length > 0 ? (
                <>
                  {(() => {
                    // Apply deliverable filter on scoped data
                    const filtered = deliverableFilter === "all"
                      ? scopedParticipants
                      : scopedParticipants.filter(p => {
                          const mod = p.scopedModules.find(m => m.moduleId === deliverableFilter);
                          if (!mod) return false;
                          if (deliverableStatusFilter === "all") return true;
                          if (deliverableStatusFilter === "entregado") return mod.status === "entregado";
                          if (deliverableStatusFilter === "pendiente") return mod.status === "pendiente";
                          return true;
                        });

                    const visibleModules = deliverableFilter === "all"
                      ? (scopedParticipants[0]?.scopedModules || [])
                      : (scopedParticipants[0]?.scopedModules.filter(m => m.moduleId === deliverableFilter) || []);

                    if (filtered.length === 0) {
                      return <p className="text-center text-muted-foreground py-8">No hay participantes con esos criterios</p>;
                    }

                    const showRotatedHeaders = deliverableFilter === "all" && visibleModules.length > 4;
                    return (
                      <>
                        {/* Leyenda */}
                        <div className="flex items-center gap-4 mb-3 text-xs flex-wrap">
                          <Badge variant="success" className="gap-1"><CheckCircle2 className="h-3 w-3" /> Entregado</Badge>
                          <Badge variant="warning" className="gap-1"><AlertTriangle className="h-3 w-3" /> Requiere revisión</Badge>
                          <Badge variant="ghost" className="gap-1"><Clock className="h-3 w-3" /> Pendiente</Badge>
                          <span className="text-muted-foreground ml-auto">Pasa el cursor sobre cada celda para ver detalles</span>
                        </div>
                        <TooltipProvider delayDuration={150}>
                        <div className="overflow-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="sticky left-0 bg-background z-10 min-w-[200px]">Participante</TableHead>
                                <TableHead>Empresa</TableHead>
                                <TableHead>Área</TableHead>
                                <TableHead className="text-center min-w-[140px]">% Contenido</TableHead>
                                <TableHead className="text-center">Entregables</TableHead>
                                <TableHead className="text-center">Diagnóstico</TableHead>
                                <TableHead className="text-center">Proyectos</TableHead>
                                <TableHead className="text-center">Lab. Retos</TableHead>
                                {visibleModules.map((m, i) => {
                                  const truncated = m.moduleName.length > 18 ? m.moduleName.substring(0, 18) + '…' : m.moduleName;
                                  if (showRotatedHeaders) {
                                    return (
                                      <TableHead key={m.moduleId} className="text-center align-bottom h-32 px-1" title={m.moduleName}>
                                        <div className="flex flex-col items-center gap-1">
                                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">M{i + 1}</Badge>
                                          <div className="-rotate-45 origin-bottom-left whitespace-nowrap text-xs font-medium translate-y-2 translate-x-2">
                                            {truncated}
                                          </div>
                                        </div>
                                      </TableHead>
                                    );
                                  }
                                  return (
                                    <TableHead key={m.moduleId} className="text-center min-w-[120px]" title={m.moduleName}>
                                      <div className="flex flex-col items-center gap-1">
                                        {deliverableFilter === "all" && (
                                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">M{i + 1}</Badge>
                                        )}
                                        <span className="text-xs font-medium">{truncated}</span>
                                      </div>
                                    </TableHead>
                                  );
                                })}
                                {deliverableFilter === "all" && <TableHead className="text-center">Avance</TableHead>}
                                <TableHead className="text-center">Acciones</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filtered.map((participant) => {
                                const downloadable = participant.scopedModules.filter(m => visibleModules.some(vm => vm.moduleId === m.moduleId) && m.fileUrl);
                                const deliveredTitles = participant.scopedModules.filter(m => m.status === 'entregado').map(m => m.moduleName);
                                const deliveredPreview = deliveredTitles.slice(0, 2).map(t => t.length > 14 ? t.substring(0, 14) + '…' : t).join(', ');
                                const extra = deliveredTitles.length - 2;
                                const projectsSubmitted = participant.projectItems.filter(pr => pr.submitted).length;
                                return (
                                  <TableRow key={participant.id} className="hover:bg-muted/40">
                                    <TableCell className="font-medium sticky left-0 bg-background">
                                      <div className="flex flex-col">
                                        <span>{participant.name}</span>
                                        {deliverableFilter === "all" && deliveredTitles.length > 0 && (
                                          <span className="text-[11px] text-muted-foreground mt-0.5">
                                            ✅ {deliveredPreview}{extra > 0 && ` +${extra}`}
                                          </span>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-sm">{participant.company}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className="text-xs">{participant.area}</Badge>
                                    </TableCell>
                                    <TableCell
                                      className="text-center cursor-pointer hover:bg-muted/40"
                                      onClick={() => setContentModalUser(participant)}
                                    >
                                      <div className="flex items-center gap-2 justify-center">
                                        <Progress value={participant.contentPercent} className="w-14 h-2" />
                                        <Badge variant={participant.contentPercent >= 70 ? 'success' : participant.contentPercent >= 30 ? 'warning' : 'ghost'} className="text-[11px]">
                                          {participant.contentPercent}%
                                        </Badge>
                                      </div>
                                    </TableCell>
                                    <TableCell
                                      className="text-center cursor-pointer hover:bg-muted/40"
                                      onClick={() => setDeliverableModalUser(participant)}
                                    >
                                      <Badge variant={participant.deliverablesSubmitted === participant.deliverablesTotal && participant.deliverablesTotal > 0 ? 'success' : participant.deliverablesSubmitted > 0 ? 'warning' : 'ghost'} className="text-[11px]">
                                        {participant.deliverablesSubmitted}/{participant.deliverablesTotal}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-center">
                                      {participant.diagnosticCompleted ? (
                                        <Badge variant="success" className="gap-1 text-[11px]"><Check className="h-3 w-3" /> Sí</Badge>
                                      ) : (
                                        <Badge variant="ghost" className="text-[11px]">—</Badge>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      className="text-center cursor-pointer hover:bg-muted/40"
                                      onClick={() => participant.projectItems.length > 0 && setProjectsModalUser(participant)}
                                    >
                                      <Badge variant={projectsSubmitted > 0 ? 'success' : participant.projectItems.length > 0 ? 'warning' : 'ghost'} className="text-[11px]">
                                        {projectsSubmitted}/{participant.projectItems.length}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-center">
                                      {participant.challengeLabSolutions > 0 ? (
                                        <Badge variant="success" className="gap-1 text-[11px] whitespace-nowrap">
                                          {participant.challengeLabChallenges > 0
                                            ? `${participant.challengeLabChallenges} ${participant.challengeLabChallenges === 1 ? 'reto' : 'retos'} · ${participant.challengeLabSolutions} ${participant.challengeLabSolutions === 1 ? 'sol.' : 'sols.'}`
                                            : `${participant.challengeLabSolutions} ${participant.challengeLabSolutions === 1 ? 'sol.' : 'sols.'}`}
                                        </Badge>
                                      ) : (
                                        <Badge variant="ghost" className="text-[11px]">—</Badge>
                                      )}
                                    </TableCell>
                                    {visibleModules.map((vm) => {
                                      const module = participant.scopedModules.find(m => m.moduleId === vm.moduleId)!;
                                      const badgeVariant: "success" | "warning" | "ghost" =
                                        module.status === 'entregado' ? 'success'
                                        : module.status === 'tardío' ? 'warning'
                                        : 'ghost';
                                      const badgeLabel =
                                        module.status === 'entregado' ? 'OK'
                                        : module.status === 'tardío' ? '⚠'
                                        : '—';
                                      const badgeIcon =
                                        module.status === 'entregado' ? <CheckCircle2 className="h-3 w-3" />
                                        : module.status === 'tardío' ? <AlertTriangle className="h-3 w-3" />
                                        : <Clock className="h-3 w-3" />;
                                      return (
                                        <TableCell key={vm.moduleId} className="text-center">
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <div className="flex flex-col items-center gap-1 cursor-default">
                                                <Badge variant={badgeVariant} className="px-2 py-0.5 text-[11px] gap-1">
                                                  {badgeIcon}
                                                  {badgeLabel}
                                                </Badge>
                                                {module.score !== null && (
                                                  <span className={`text-xs font-semibold ${getScoreColor(module.score)}`}>
                                                    {module.score}
                                                  </span>
                                                )}
                                              </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="max-w-xs">
                                              <div className="space-y-1 text-xs">
                                                <div className="font-semibold">{module.deliverableName || vm.moduleName}</div>
                                                <div className="text-muted-foreground">Sesión en Vivo: {vm.moduleName}</div>
                                                <div>Estado: <span className="font-medium capitalize">{module.status}</span></div>
                                                {module.deliveredAt && (
                                                  <div>Entregado: {new Date(module.deliveredAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                                )}
                                                {module.score !== null && <div>Score: <span className="font-semibold">{module.score}</span></div>}
                                                {module.fileUrl && (
                                                  <button
                                                    onClick={() => window.open(module.fileUrl!, '_blank')}
                                                    className="inline-flex items-center gap-1 mt-1 text-primary hover:underline"
                                                  >
                                                    <Download className="h-3 w-3" /> Descargar archivo
                                                  </button>
                                                )}
                                              </div>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TableCell>
                                      );
                                    })}
                                    {deliverableFilter === "all" && (
                                      <TableCell className="text-center">
                                        <div className="flex items-center gap-2 justify-center">
                                          <Progress value={participant.deliverablesTotal > 0 ? (participant.deliverablesSubmitted / participant.deliverablesTotal) * 100 : 0} className="w-16 h-2" />
                                          <span className="text-xs text-muted-foreground">{participant.deliverablesSubmitted}/{participant.deliverablesTotal}</span>
                                        </div>
                                      </TableCell>
                                    )}
                                    <TableCell className="text-center">
                                      {downloadable.length > 0 ? (
                                        downloadable.length === 1 ? (
                                          <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => window.open(downloadable[0].fileUrl!, '_blank')}>
                                            <Download className="h-3 w-3" />
                                            <span className="text-xs">Descargar</span>
                                          </Button>
                                        ) : (
                                          <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => downloadable.forEach((d, i) => setTimeout(() => window.open(d.fileUrl!, '_blank'), i * 300))}>
                                            <Download className="h-3 w-3" />
                                            <span className="text-xs">Descargar ({downloadable.length})</span>
                                          </Button>
                                        )
                                      ) : (
                                        <span className="text-xs text-muted-foreground">Sin archivos</span>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                        </TooltipProvider>
                      </>
                    );
                  })()}
                </>
              ) : (
                <p className="text-center text-muted-foreground py-12">Selecciona una cohorte para ver los entregables</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="space-y-4">
          {/* Toolbar: filter + AI analysis */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3">
            <label className="flex items-center gap-3 text-sm cursor-pointer">
              <Switch
                checked={projOnlyWithDeliverable}
                onCheckedChange={setProjOnlyWithDeliverable}
              />
              <span className="font-medium">Solo proyectos con entregable</span>
              <span className="text-xs text-muted-foreground">(filtra KPIs y gráficas)</span>
            </label>
            <Button
              size="sm"
              variant="outline"
              className="gap-2"
              onClick={() => setProjectsAnalysisOpen(true)}
              disabled={!effectiveOrg || effectiveOrg === 'all'}
            >
              <Sparkles className="h-4 w-4 text-primary" />
              Análisis IA de proyectos
            </Button>
          </div>

          {(() => {
            return (
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card><CardContent className="pt-6 text-center"><p className="text-3xl font-bold text-primary">{projectResults.total}</p><p className="text-sm text-muted-foreground">Total Proyectos</p></CardContent></Card>
                <Card><CardContent className="pt-6 text-center"><p className="text-3xl font-bold text-green-500">{projectResults.withDeliverable}</p><p className="text-sm text-muted-foreground">Con Entregable</p></CardContent></Card>
                <Card><CardContent className="pt-6 text-center"><p className="text-3xl font-bold text-amber-500">{projectResults.fromChallengeLab}</p><p className="text-sm text-muted-foreground">Desde Lab. Retos</p></CardContent></Card>
                <Card><CardContent className="pt-6 text-center"><p className="text-3xl font-bold text-primary">{projectResults.avgScore}</p><p className="text-sm text-muted-foreground">Score Promedio</p></CardContent></Card>
              </div>
            );
          })()}

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Estado de Proyectos</CardTitle>
                <CardDescription>Distribución por estado actual</CardDescription>
              </CardHeader>
              <CardContent>
                {projectResults.byStatus.length > 0 ? (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie data={projectResults.byStatus} cx="50%" cy="50%" outerRadius={80} dataKey="count" nameKey="status" label={({ status, count }) => `${status}: ${count}`}>
                          {projectResults.byStatus.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin proyectos</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Proyectos por Tipo de Solución</CardTitle>
                <CardDescription>Distribución por categoría de solución (excluye sin categoría)</CardDescription>
              </CardHeader>
              <CardContent>
                {projectResults.bySolutionType.length > 0 ? (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={projectResults.bySolutionType}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="count"
                          nameKey="type"
                          label={({ type, count, percentage }) => `${type}: ${count} (${percentage}%)`}
                        >
                          {projectResults.bySolutionType.map((entry, index) => (
                            <Cell key={`cell-sol-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip formatter={(value: any, _name: any, props: any) => [`${value} proyectos (${props.payload.percentage}%)`, props.payload.type]} />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin proyectos categorizados</p>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Herramientas en Proyectos</CardTitle>
                <CardDescription>Herramientas más usadas en los proyectos</CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const toolCounts: Record<string, number> = {};
                  projectResults.userLog.forEach(u => {
                    if (u.projectTools) {
                      u.projectTools.forEach(t => { toolCounts[t] = (toolCounts[t] || 0) + 1; });
                    }
                  });
                  const toolData = Object.entries(toolCounts).map(([tool, count]) => ({ tool, count })).sort((a, b) => b.count - a.count);
                  return toolData.length > 0 ? (
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={toolData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="tool" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} />
                          <RechartsTooltip />
                          <Bar dataKey="count" fill="hsl(var(--secondary))" radius={[4, 4, 0, 0]} name="Proyectos" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-12">Sin herramientas registradas</p>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          {projectResults.topProjects.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Proyectos</CardTitle>
                <CardDescription>Proyectos mejor evaluados</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {projectResults.topProjects.map((project, index) => (
                    <div key={project.name} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">{index + 1}</div>
                        <div>
                          <p className="font-medium">{project.name}</p>
                          <p className="text-sm text-muted-foreground">{project.area}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={project.impact === "Alto" ? "default" : "secondary"}>{project.impact}</Badge>
                        <span className="text-lg font-bold text-primary">{project.score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Project User Log */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Log de Usuarios - Proyectos
                </CardTitle>
                <CardDescription>Seguimiento de usuarios con y sin proyectos asignados</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleExportProjectLog} variant="outline" size="sm" className="gap-2">
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
                <Button onClick={() => handleExportTabPDF('projects')} variant="outline" size="sm" className="gap-2">
                  <FileCheck className="h-4 w-4" />
                  PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Buscar por nombre, email o proyecto..." value={projSearchTerm} onChange={(e) => setProjSearchTerm(e.target.value)} className="pl-9" />
                </div>
                <Select value={projFilterStatus} onValueChange={setProjFilterStatus}>
                  <SelectTrigger className="w-[160px]"><SelectValue placeholder="Estado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="hasProject">Con proyecto</SelectItem>
                    <SelectItem value="noProject">Sin proyecto</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={projFilterArea} onValueChange={setProjFilterArea}>
                  <SelectTrigger className="w-[160px]"><SelectValue placeholder="Área" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las áreas</SelectItem>
                    {projAreas.map((area) => (<SelectItem key={area} value={area}>{area}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-md border overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Área</TableHead>
                      <TableHead className="text-center">Estado</TableHead>
                      <TableHead>Proyecto</TableHead>
                      <TableHead>Estado Proyecto</TableHead>
                      <TableHead>Fecha Creación</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProjUsers.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No se encontraron usuarios</TableCell></TableRow>
                    ) : (
                      filteredProjUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell className="text-muted-foreground text-sm">{user.email}</TableCell>
                          <TableCell>{user.area}</TableCell>
                          <TableCell className="text-center">
                            {user.hasProject ? (
                              <Badge className="bg-primary/10 text-primary hover:bg-primary/20"><CheckCircle2 className="h-3 w-3 mr-1" />Con proyecto</Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-amber-500/10 text-amber-600"><Clock className="h-3 w-3 mr-1" />Sin proyecto</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">
                            {user.projectName ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button onClick={() => handleOpenProjectDetail(user)} className="cursor-pointer group flex items-center gap-2">
                                      <span className="text-primary group-hover:underline font-medium">{user.projectName}</span>
                                      <Eye className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent><p>Ver detalles</p></TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : "—"}
                          </TableCell>
                          <TableCell>
                            {user.projectStatus ? (
                              <Badge variant="outline" className={
                                user.projectStatus === "Completados" ? "border-green-500 text-green-600" :
                                user.projectStatus === "En progreso" ? "border-blue-500 text-blue-600" :
                                "border-amber-500 text-amber-600"
                              }>{user.projectStatus}</Badge>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {user.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES') : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Mostrando {filteredProjUsers.length} de {projectResults.userLog.length} usuarios</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Adoption Tab */}
        <TabsContent value="adoption" className="space-y-4">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => handleExportTabPDF('adoption')} className="gap-2">
              <FileCheck className="h-4 w-4" />PDF
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Adopción por Departamento</CardTitle>
                <CardDescription>Nivel de adopción en cada área</CardDescription>
              </CardHeader>
              <CardContent>
                {adoptionData.byDepartment.length > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={adoptionData.byDepartment} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis dataKey="dept" type="category" width={80} tick={{ fontSize: 12 }} />
                        <RechartsTooltip />
                        <Bar dataKey="adoption" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} name="Adopción %" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin datos de adopción</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Índice de Adopción</CardTitle>
                <CardDescription>Promedio de 4 ratios de uso real de la plataforma</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3">
                  <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <p className="text-4xl font-bold text-primary">{adoptionData.overallAdoption}%</p>
                    <p className="text-sm font-medium mt-1">Adopción general</p>
                    <p className="text-[11px] text-muted-foreground mt-1">Promedio de entregables, proyectos, diagnósticos y lab. retos</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {adoptionData.breakdown.deliverables ? (
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <p className="text-xl font-bold">{adoptionData.breakdown.deliverables.ratio}%</p>
                        <p className="text-xs font-medium">Entregables</p>
                        <p className="text-[10px] text-muted-foreground">{adoptionData.breakdown.deliverables.numerator} / {adoptionData.breakdown.deliverables.denominator} esperados</p>
                      </div>
                    ) : (
                      <div className="p-3 bg-muted/30 rounded-lg opacity-60">
                        <p className="text-xl font-bold">—</p>
                        <p className="text-xs font-medium">Entregables</p>
                        <p className="text-[10px] text-muted-foreground">Sin participantes en cohorte</p>
                      </div>
                    )}
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xl font-bold">{adoptionData.breakdown.projects.ratio}%</p>
                      <p className="text-xs font-medium">Proyectos</p>
                      <p className="text-[10px] text-muted-foreground">{adoptionData.breakdown.projects.numerator} / {adoptionData.breakdown.projects.denominator} participantes</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xl font-bold">{adoptionData.breakdown.diagnostics.ratio}%</p>
                      <p className="text-xs font-medium">Diagnósticos</p>
                      <p className="text-[10px] text-muted-foreground">{adoptionData.breakdown.diagnostics.numerator} / {adoptionData.breakdown.diagnostics.denominator} participantes</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-xl font-bold">{adoptionData.breakdown.challengeLab.ratio}%</p>
                      <p className="text-xs font-medium">Lab. Retos</p>
                      <p className="text-[10px] text-muted-foreground">{adoptionData.breakdown.challengeLab.numerator} / {adoptionData.breakdown.challengeLab.denominator} participantes</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                    <div className="text-center p-2">
                      <p className="text-lg font-semibold text-green-500">{adoptionData.weeklyActiveUsers}</p>
                      <p className="text-[11px] text-muted-foreground">Activos (7 días)</p>
                    </div>
                    <div className="text-center p-2">
                      <p className="text-lg font-semibold text-purple-500">{adoptionData.completionRate}%</p>
                      <p className="text-[11px] text-muted-foreground">Completado entregables</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tools Tab */}
        <TabsContent value="tools" className="space-y-4">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => handleExportTabPDF('tools')} className="gap-2">
              <FileCheck className="h-4 w-4" />PDF
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Herramientas más Usadas</CardTitle>
                <CardDescription>Top herramientas IA por nivel de uso</CardDescription>
              </CardHeader>
              <CardContent>
                {toolsUsageData.topTools.length > 0 ? (
                  <div className="space-y-4">
                    {toolsUsageData.topTools.map((tool) => (
                      <div key={tool.name} className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{tool.name}</span>
                            <Badge variant="outline" className="text-xs">{tool.category}</Badge>
                          </div>
                          <span>{tool.usage}%</span>
                        </div>
                        <Progress value={tool.usage} className="h-2" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin herramientas registradas</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Uso por Categoría</CardTitle>
                <CardDescription>Distribución de herramientas por tipo</CardDescription>
              </CardHeader>
              <CardContent>
                {toolsUsageData.byCategory.length > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={toolsUsageData.byCategory}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <RechartsTooltip />
                        <Bar dataKey="usage" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Uso %" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin datos de categorías</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <Zap className="h-8 w-8 mx-auto text-primary mb-2" />
                  <p className="text-2xl font-bold">{toolsUsageData.totalTools}</p>
                  <p className="text-sm text-muted-foreground">Herramientas totales</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  <p className="text-2xl font-bold">{toolsUsageData.activeTools}</p>
                  <p className="text-sm text-muted-foreground">En uso activo</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <AlertTriangle className="h-8 w-8 mx-auto text-amber-500 mb-2" />
                  <p className="text-2xl font-bold">{toolsUsageData.totalTools - toolsUsageData.activeTools}</p>
                  <p className="text-sm text-muted-foreground">Bajo uso</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* NPS Tab */}
        <TabsContent value="nps" className="space-y-4">
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
              <CardContent className="pt-6">
                <ThumbsUp className="h-8 w-8 text-green-500 mb-2" />
                <p className="text-3xl font-bold">{npsData.currentNPS}</p>
                <p className="text-sm text-muted-foreground">NPS Actual</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-2xl font-bold text-primary">{npsData.totalResponses}</p>
                <p className="text-sm text-muted-foreground">Total Respuestas</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-2xl font-bold text-green-600">{npsData.distribution.promoters}</p>
                <p className="text-xs text-muted-foreground">Promotores</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-2xl font-bold text-red-600">{npsData.distribution.detractors}</p>
                <p className="text-xs text-muted-foreground">Detractores</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Distribución de Respuestas</CardTitle>
                <CardDescription>Promotores vs Pasivos vs Detractores</CardDescription>
              </CardHeader>
              <CardContent>
                {npsData.totalResponses > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={[
                            { name: 'Promotores (9-10)', value: npsData.distribution.promoters, color: '#22c55e' },
                            { name: 'Pasivos (7-8)', value: npsData.distribution.passives, color: '#f59e0b' },
                            { name: 'Detractores (0-6)', value: npsData.distribution.detractors, color: '#ef4444' },
                          ]}
                          cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value"
                        >
                          <Cell fill="#22c55e" />
                          <Cell fill="#f59e0b" />
                          <Cell fill="#ef4444" />
                        </Pie>
                        <RechartsTooltip />
                        <Legend />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin respuestas NPS</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">NPS por Área</CardTitle>
                <CardDescription>Comparativa de satisfacción por departamento</CardDescription>
              </CardHeader>
              <CardContent>
                {npsData.byArea.length > 0 ? (
                  <div className="grid grid-cols-2 gap-4">
                    {npsData.byArea.map((area) => (
                      <div key={area.area} className="text-center p-4 rounded-lg bg-muted/50">
                        <p className={`text-2xl font-bold ${area.nps >= 70 ? 'text-green-600' : area.nps >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{area.nps}</p>
                        <p className="text-sm font-medium">{area.area}</p>
                        <p className="text-xs text-muted-foreground">{area.responses} respuestas</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin datos por área</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* User NPS Table */}
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Detalle por Usuario
                  </CardTitle>
                  <CardDescription>Clasificación NPS individual</CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Select value={npsFilterCohort} onValueChange={setNpsFilterCohort}>
                    <SelectTrigger className="w-[150px]"><SelectValue placeholder="Cohorte" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las cohortes</SelectItem>
                      {npsCohorts.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Select value={npsFilterCompany} onValueChange={setNpsFilterCompany}>
                    <SelectTrigger className="w-[150px]"><SelectValue placeholder="Empresa" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {npsCompanies.map(c => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Select value={npsFilterType} onValueChange={setNpsFilterType}>
                    <SelectTrigger className="w-[140px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="promoter">Promotores</SelectItem>
                      <SelectItem value="passive">Pasivos</SelectItem>
                      <SelectItem value="detractor">Detractores</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={handleExportNPS} disabled={filteredNpsUsers.length === 0} className="gap-2">
                    <Download className="h-4 w-4" />CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExportTabPDF('nps')} disabled={filteredNpsUsers.length === 0} className="gap-2">
                    <FileCheck className="h-4 w-4" />PDF
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Participante</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Cohorte</TableHead>
                      <TableHead>Área</TableHead>
                      <TableHead className="text-center">Puntuación</TableHead>
                      <TableHead className="text-center">Tipo</TableHead>
                      <TableHead className="max-w-[220px]">Comentario</TableHead>
                      <TableHead className="text-right">Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredNpsUsers.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No se encontraron resultados</TableCell></TableRow>
                    ) : (
                      filteredNpsUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell className="text-muted-foreground">{user.company}</TableCell>
                          <TableCell><Badge variant="outline" className="font-normal">{user.cohort}</Badge></TableCell>
                          <TableCell>{user.area}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-bold ${user.score >= 9 ? 'text-green-600' : user.score >= 7 ? 'text-amber-600' : 'text-red-600'}`}>{user.score}</span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary" className={
                              user.type === 'promoter' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                              user.type === 'passive' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                              'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            }>
                              {user.type === 'promoter' && <><ThumbsUp className="h-3 w-3 mr-1" /> Promotor</>}
                              {user.type === 'passive' && <><Meh className="h-3 w-3 mr-1" /> Pasivo</>}
                              {user.type === 'detractor' && <><ThumbsDown className="h-3 w-3 mr-1" /> Detractor</>}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[220px]">
                            {user.comment ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="block truncate text-sm text-muted-foreground cursor-help">
                                      {user.comment}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="whitespace-pre-wrap text-sm">{user.comment}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span className="text-muted-foreground/50">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground text-sm">
                            {new Date(user.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ROI Tab */}
        <TabsContent value="roi" className="space-y-4">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => handleExportTabPDF('roi')} className="gap-2">
              <FileCheck className="h-4 w-4" />PDF
            </Button>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
              <CardContent className="pt-6">
                <DollarSign className="h-8 w-8 text-emerald-500 mb-2" />
                <p className="text-2xl font-bold">{formatCurrency(roiData.totalInvestment)}</p>
                <p className="text-sm text-muted-foreground">Inversión Total</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
              <CardContent className="pt-6">
                <TrendingUp className="h-8 w-8 text-green-500 mb-2" />
                <p className="text-2xl font-bold">{formatCurrency(roiData.totalBenefits)}</p>
                <p className="text-sm text-muted-foreground">Beneficios Totales</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
              <CardContent className="pt-6">
                <PieChart className="h-8 w-8 text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{roiData.roiPercentage}%</p>
                <p className="text-sm text-muted-foreground">ROI</p>
                <p className="text-xs text-muted-foreground mt-1">BCR: {roiData.bcrRatio}x</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
              <CardContent className="pt-6">
                <Clock className="h-8 w-8 text-amber-500 mb-2" />
                <p className="text-2xl font-bold">{roiData.paybackMonths} meses</p>
                <p className="text-sm text-muted-foreground">Período de Recuperación</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Desglose de Costos</CardTitle>
                <CardDescription>Distribución de la inversión por categoría</CardDescription>
              </CardHeader>
              <CardContent>
                {roiData.costBreakdown.length > 0 ? (
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie data={roiData.costBreakdown} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="amount" nameKey="category">
                          {roiData.costBreakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip formatter={(value: number) => formatCurrency(value)} />
                        <Legend />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin datos de costos</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Inventario de Beneficios</CardTitle>
                <CardDescription>Distribución del retorno por tipo</CardDescription>
              </CardHeader>
              <CardContent>
                {roiData.benefitBreakdown.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {roiData.benefitBreakdown.map((item) => {
                      const pct = roiData.totalBenefits > 0 ? Math.round((item.amount / roiData.totalBenefits) * 100) : 0;
                      return (
                        <div key={item.category} className="p-4 rounded-xl bg-muted/50 space-y-2">
                          <div className="flex justify-between items-start">
                            <h4 className="text-sm font-medium">{item.category}</h4>
                            <Badge variant="outline" className="text-xs">{pct}%</Badge>
                          </div>
                          <p className="text-xl font-bold text-green-600">{formatCurrency(item.amount)}</p>
                          <Progress value={pct} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-12">Sin beneficios registrados</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top projects by ROI */}
          {roiData.projects.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Proyectos por ROI</CardTitle>
                <CardDescription>Mejores retornos según modelo de evaluación</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proyecto</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead className="text-right">Costos</TableHead>
                      <TableHead className="text-right">Beneficios</TableHead>
                      <TableHead className="text-right">ROI</TableHead>
                      <TableHead className="text-right">BCR</TableHead>
                      <TableHead className="text-right">Payback</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...roiData.projects].sort((a, b) => b.roi - a.roi).map((p) => (
                      <TableRow key={p.projectId}>
                        <TableCell className="font-medium">{p.projectName}</TableCell>
                        <TableCell>{p.company}</TableCell>
                        <TableCell className="text-right text-destructive">{formatCurrency(p.totalCosts)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(p.totalBenefits)}</TableCell>
                        <TableCell className="text-right">
                          <Badge className={p.roi > 200 ? "bg-green-500/20 text-green-600" : "bg-amber-500/20 text-amber-600"}>
                            {Math.round(p.roi)}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">{p.bcr.toFixed(2)}x</TableCell>
                        <TableCell className="text-right text-muted-foreground">{p.paybackMonths.toFixed(1)} meses</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Project Detail Dialog */}
      <Dialog open={isProjectDialogOpen} onOpenChange={setIsProjectDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              {selectedProjectUser?.projectName}
            </DialogTitle>
            <DialogDescription>Proyecto de {selectedProjectUser?.name}</DialogDescription>
          </DialogHeader>
          {selectedProjectUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={
                  selectedProjectUser.projectStatus === "Completados" ? "border-green-500 text-green-600" :
                  selectedProjectUser.projectStatus === "En progreso" ? "border-blue-500 text-blue-600" :
                  "border-amber-500 text-amber-600"
                }>{selectedProjectUser.projectStatus}</Badge>
                {selectedProjectUser.projectImpact && <Badge variant={selectedProjectUser.projectImpact === "Alto" ? "default" : "secondary"}>Impacto {selectedProjectUser.projectImpact}</Badge>}
                {selectedProjectUser.projectScore && <Badge className="bg-primary/10 text-primary">Score: {selectedProjectUser.projectScore}</Badge>}
              </div>
              {selectedProjectUser.projectDescription && (
                <div><h4 className="text-sm font-medium text-muted-foreground mb-1">Descripción</h4><p className="text-sm">{selectedProjectUser.projectDescription}</p></div>
              )}
              {selectedProjectUser.projectTools && selectedProjectUser.projectTools.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Herramientas utilizadas</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProjectUser.projectTools.map((tool) => (
                      <Badge key={tool} variant="outline" className="bg-muted"><Zap className="h-3 w-3 mr-1" />{tool}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                <div><h4 className="text-xs font-medium text-muted-foreground">Área</h4><p className="text-sm font-medium">{selectedProjectUser.area}</p></div>
                <div><h4 className="text-xs font-medium text-muted-foreground">Fecha de creación</h4><p className="text-sm font-medium">{selectedProjectUser.createdAt ? new Date(selectedProjectUser.createdAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : "—"}</p></div>
              </div>
              <div className="pt-3 border-t">
                <h4 className="text-xs font-medium text-muted-foreground mb-2">Responsable</h4>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                    {selectedProjectUser.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{selectedProjectUser.name}</p>
                    <p className="text-xs text-muted-foreground">{selectedProjectUser.email}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diagnostic Detail Dialog */}
      <Dialog open={isDiagDialogOpen} onOpenChange={setIsDiagDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              Respuestas del Diagnóstico
            </DialogTitle>
            <DialogDescription>Resultados de {selectedDiagUser?.name}</DialogDescription>
          </DialogHeader>
          {selectedDiagUser && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                  {selectedDiagUser.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{selectedDiagUser.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedDiagUser.email}</p>
                </div>
                <div className="text-right">
                  <Badge className="bg-primary/10 text-primary mb-1">{selectedDiagUser.level}</Badge>
                  <p className="text-2xl font-bold text-primary">{selectedDiagUser.score}/5</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><h4 className="text-xs font-medium text-muted-foreground">Área</h4><p className="text-sm font-medium">{selectedDiagUser.area}</p></div>
                <div><h4 className="text-xs font-medium text-muted-foreground">Fecha de completado</h4><p className="text-sm font-medium">{selectedDiagUser.completedAt ? new Date(selectedDiagUser.completedAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : "—"}</p></div>
              </div>
              {selectedDiagUser.responses && selectedDiagUser.responses.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary" />
                    Respuestas del participante
                  </h4>
                  <div className="space-y-4">
                    {selectedDiagUser.responses.map((response: any, index: number) => (
                      <div key={index} className="p-4 bg-muted/30 rounded-lg border border-border/50">
                        <p className="text-sm font-medium text-primary mb-2">{index + 1}. {response.question}</p>
                        <p className={`text-sm leading-relaxed ${response.answer === 'No puede responder' ? 'text-muted-foreground italic' : 'text-foreground'}`}>{response.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Content detail modal */}
      <Dialog open={!!contentModalUser} onOpenChange={(o) => !o && setContentModalUser(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contenido visto — {contentModalUser?.name}</DialogTitle>
            <DialogDescription>
              {progressScopeMode === 'total'
                ? 'Mostrando todo la ruta'
                : `Mostrando hasta semana ${cohortData.currentWeekNumber}`}
              {contentModalUser && ` · ${contentModalUser.scopedContent.filter(c => c.completed).length}/${contentModalUser.scopedContent.length} completados (${contentModalUser.contentPercent}%)`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {contentModalUser?.scopedContent.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No hay contenido en este alcance</p>
            ) : (
              contentModalUser?.scopedContent.map(c => (
                <div key={c.id} className="flex items-center justify-between p-2 rounded-md border bg-card">
                  <div className="flex items-center gap-2 min-w-0">
                    {c.completed ? (
                      <Check className="h-4 w-4 text-success shrink-0" />
                    ) : (
                      <X className="h-4 w-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="text-sm truncate">{c.title}</span>
                  </div>
                  {c.weekNumber !== null && (
                    <Badge variant="outline" className="text-[10px] shrink-0">S{c.weekNumber}</Badge>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Deliverable detail modal */}
      <Dialog open={!!deliverableModalUser} onOpenChange={(o) => !o && setDeliverableModalUser(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Entregables — {deliverableModalUser?.name}</DialogTitle>
            <DialogDescription>
              {progressScopeMode === 'total'
                ? 'Mostrando todo la ruta'
                : `Mostrando hasta semana ${cohortData.currentWeekNumber}`}
              {deliverableModalUser && ` · ${deliverableModalUser.deliverablesSubmitted}/${deliverableModalUser.deliverablesTotal} entregados`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {deliverableModalUser?.scopedModules.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No hay entregables en este alcance</p>
            ) : (
              deliverableModalUser?.scopedModules.map(m => {
                const variant: 'success' | 'warning' | 'ghost' =
                  m.status === 'entregado' ? 'success' : m.status === 'tardío' ? 'warning' : 'ghost';
                const label = m.status === 'entregado' ? 'Entregado' : m.status === 'tardío' ? 'Requiere revisión' : 'Pendiente';
                return (
                  <div key={m.moduleId} className="flex items-center justify-between p-2 rounded-md border bg-card gap-2">
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium truncate">{m.deliverableName || m.moduleName}</span>
                      <span className="text-xs text-muted-foreground truncate">Sesión en Vivo: {m.moduleName}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant={variant} className="text-[11px]">{label}</Badge>
                      {m.fileUrl && (
                        <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => window.open(m.fileUrl!, '_blank')}>
                          <Download className="h-3 w-3" />
                          <span className="text-xs">Descargar</span>
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Projects detail modal */}
      <Dialog open={!!projectsModalUser} onOpenChange={(o) => !o && setProjectsModalUser(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Proyectos — {projectsModalUser?.name}</DialogTitle>
            <DialogDescription>
              {projectsModalUser && `${projectsModalUser.projectItems.filter(p => p.submitted).length}/${projectsModalUser.projectItems.length} enviados`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {projectsModalUser?.projectItems.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No hay proyectos creados</p>
            ) : (
              projectsModalUser?.projectItems.map(p => (
                <div key={p.id} className="flex items-center justify-between p-2 rounded-md border bg-card gap-2">
                  <span className="text-sm font-medium truncate">{p.name}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={p.submitted ? 'success' : 'warning'} className="text-[11px]">
                      {p.submitted ? 'Enviado' : 'En curso'}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1"
                      onClick={() => navigate(`/ai-portfolio/${p.id}`)}
                    >
                      <ExternalLink className="h-3 w-3" />
                      <span className="text-xs">Ver</span>
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <DiagnosticAnalysisModal
        open={analysisOpen}
        onOpenChange={setAnalysisOpen}
        organizationId={effectiveOrg}
        cohortId={selectedCohort}
      />

      <ProjectsAnalysisModal
        open={projectsAnalysisOpen}
        onOpenChange={setProjectsAnalysisOpen}
        organizationId={effectiveOrg}
        cohortId={selectedCohort}
      />
    </div>
  );
}

// CSV helper
function downloadCsv(headers: string[], rows: string[][], filename: string) {
  const csvContent = [headers.join(","), ...rows.map(row => row.map(v => `"${v}"`).join(","))].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.setAttribute("href", URL.createObjectURL(blob));
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
